import { useEffect, useRef } from 'react';
import { useRichTextEditor } from './RichTextEditorProvider';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { Box } from '@mui/material';
import { sharedContentStyles } from './richTextEditorStyles';
import { INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import { KEY_DOWN_COMMAND, COMMAND_PRIORITY_NORMAL, $getRoot, $insertNodes } from 'lexical';
import { $isRootTextContentEmpty } from '@lexical/text';
import { Placeholder } from './Placeholder';

const LexicalEditor = ({
  children,
  placeholder,
}: {
  children?: React.ReactNode;
  placeholder?: string;
}) => {
  return (
    <Box className="editor-container" sx={{ '&.editor-container': sharedContentStyles }}>
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            className="editor-content"
            style={{
              minHeight: '100px',
              outline: 'none',
              padding: '8px',
            }}
          />
        }
        placeholder={<Placeholder placeholder={placeholder} />}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <ListPlugin />
      <LinkPlugin attributes={{ target: '_blank', rel: 'noopener noreferrer' }} />
      {children}
    </Box>
  );
};

// Editor Component - Renders the actual editor with toolbar
export const RichTextEditor = ({
  onChange,
  onBlur,
  initialContent,
  placeholder,
  autoFocus,
}: {
  onChange?: (richText: string) => void;
  onBlur?: () => void;
  initialContent?: string;
  placeholder?: string;
  autoFocus?: boolean;
}) => {
  const { editor } = useRichTextEditor();
  const isInitialized = useRef(false);
  const currentContentRef = useRef<string>(initialContent || '');

  // Register keyboard shortcut for bullet list (Ctrl+Shift+8 or Cmd+Shift+8)
  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === '8') {
          event.preventDefault();
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor]);

  // Set initial content only on mount and focus the editor
  useEffect(() => {
    if (initialContent && !isInitialized.current) {
      isInitialized.current = true;
      editor.update(() => {
        // Parse HTML and create Lexical nodes
        const parser = new DOMParser();
        const dom = parser.parseFromString(initialContent, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);

        // Clear existing content and insert new nodes
        const root = $getRoot();
        root.clear();
        root.select();
        $insertNodes(nodes);
      });
    }

    // Register blur event handler
    if (onBlur) {
      return editor.registerRootListener((rootElement, prevRootElement) => {
        if (prevRootElement) {
          prevRootElement.removeEventListener('blur', onBlur);
        }
        if (rootElement) {
          rootElement.addEventListener('blur', onBlur);
        }
      });
    }
  }, [editor, onBlur, initialContent]);

  useEffect(() => {
    if (autoFocus) {
      // Use queueMicrotask to defer focus until after React has updated the DOM
      queueMicrotask(() => {
        editor.focus();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When editor state changes, propagate HTML
  useEffect(() => {
    const unsubscribe = editor.registerUpdateListener(({ editorState }: any) => {
      try {
        editorState.read(() => {
          const isEmpty = $isRootTextContentEmpty(editor.isComposing());

          const htmlContent = isEmpty ? '' : $generateHtmlFromNodes(editor, null);
          // Store current content in ref for blur handler
          currentContentRef.current = htmlContent;

          // Only call onChange for real-time updates
          // onBlur will be handled by the blur event listener above
          if (htmlContent !== initialContent) {
            onChange?.(htmlContent);
          }
        });
      } catch (error) {
        console.error('Error exporting editor content:', error);
      }
    });

    return unsubscribe;
  }, [editor, onChange, initialContent]);

  return <LexicalEditor placeholder={placeholder} />;
};
